import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import SchoolService from "./Locality/Service/schoolService";
import EnquiryService from "./Locality/Service/enquiryService";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    background: "#f6f7fb",
    padding: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
  },
  shell: {
    maxWidth: 1000,
    margin: "0 auto",
  },
  headerCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
  },
  brandRow: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      textAlign: "center",
      justifyItems: "center",
    },
  },
  logo: {
    width: 54,
    height: 54,
    borderRadius: 12,
    objectFit: "cover",
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff",
  },
  title: {
    fontWeight: 800,
    letterSpacing: 0.2,
  },
  subtitle: {
    color: "rgba(0,0,0,0.65)",
    marginTop: theme.spacing(0.5),
  },
  yearChip: {
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 999,
    background: "rgba(25, 118, 210, 0.10)",
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  formCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
  },
  sectionTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  helper: {
    color: "rgba(0,0,0,0.6)",
    marginBottom: theme.spacing(2),
  },
  footer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  primaryButton: {
    borderRadius: 12,
    height: 44,
    fontWeight: 800,
  },
  danger: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
}));

const ACADEMIC_YEAR = "2026-27";
const CLASS_OPTIONS = ["Toddler", "Primary", "Lower Elementary", "Upper Elementary", "Adolescent"];
const ADMISSION_OPTIONS = ["Regular school", "Summer camp"];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const tenDigitRegex = /^[0-9]{10}$/;

function formatDDMMYYYY(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function isoToDDMMYYYY(iso) {
  const s = String(iso || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function EnquiryFormPublicPage() {
  const classes = useStyles();
  const { schoolId } = useParams();

  const [schoolName, setSchoolName] = useState("");
  const [schoolLogo, setSchoolLogo] = useState("");
  const [isSchoolLoading, setIsSchoolLoading] = useState(true);
  const [schoolError, setSchoolError] = useState("");

  const systemDate = useMemo(() => new Date(), []);
  const systemDateText = useMemo(() => formatDDMMYYYY(systemDate), [systemDate]);

  const [values, setValues] = useState({
    studentName: "",
    dob: "",
    gender: "",
    fatherName: "",
    fatherContactNumber: "",
    motherName: "",
    motherContactNumber: "",
    email: "",
    classOrAgeGroup: "",
    admissionSoughtFor: "",
    additionalInformation: "",
  });

  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setIsSchoolLoading(true);
    setSchoolError("");

    SchoolService.getSchooleRegistrationById(schoolId)
      .then((res) => {
        if (!mounted) return;
        setSchoolName(res?.schooleName || "Enquiry Form");
        setSchoolLogo(res?.logo || "");
      })
      .catch(() => {
        if (!mounted) return;
        setSchoolError("Unable to load school details. Please check the link and try again.");
      })
      .finally(() => {
        if (!mounted) return;
        setIsSchoolLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [schoolId]);

  const errors = useMemo(() => {
    const e = {};
    if (!values.studentName?.trim()) e.studentName = "Student Name is required";
    if (!values.dob?.trim()) e.dob = "DOB is required";
    if (!values.gender) e.gender = "Gender is required";

    if (!values.fatherName?.trim()) e.fatherName = "Father name is required";
    if (!values.fatherContactNumber?.trim()) e.fatherContactNumber = "Father contact number is required";
    else if (!tenDigitRegex.test(values.fatherContactNumber.trim())) e.fatherContactNumber = "Enter 10 digit mobile number";

    if (!values.motherName?.trim()) e.motherName = "Mother name is required";
    if (!values.motherContactNumber?.trim()) e.motherContactNumber = "Mother contact number is required";
    else if (!tenDigitRegex.test(values.motherContactNumber.trim())) e.motherContactNumber = "Enter 10 digit mobile number";

    if (!values.email?.trim()) e.email = "Email is required";
    else if (!emailRegex.test(values.email.trim())) e.email = "Enter a valid email";
    return e;
  }, [values]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleChange = (key) => (event) => {
    const next = event?.target?.value;
    setSubmitError("");
    setSubmitSuccess("");

    if (key === "fatherContactNumber" || key === "motherContactNumber") {
      const digitsOnly = String(next || "").replace(/[^0-9]/g, "").slice(0, 10);
      setValues((prev) => ({ ...prev, [key]: digitsOnly }));
      return;
    }
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const markTouched = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  useEffect(() => {
    if (!submitSuccess) return undefined;
    const t = setTimeout(() => setSubmitSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  const onSubmit = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setTouched((t) => ({
      ...t,
      studentName: true,
      dob: true,
      gender: true,
      fatherName: true,
      fatherContactNumber: true,
      motherName: true,
      motherContactNumber: true,
      email: true,
    }));

    if (hasErrors) {
      setSubmitError("Please fill all mandatory fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      await EnquiryService.submitPublicEnquiry(schoolId, {
        ...values,
        dob: isoToDDMMYYYY(values.dob),
        academicYear: ACADEMIC_YEAR,
      });
      setSubmitSuccess("Enquiry submitted successfully.");
      setValues({
        studentName: "",
        dob: "",
        gender: "",
        fatherName: "",
        fatherContactNumber: "",
        motherName: "",
        motherContactNumber: "",
        email: "",
        classOrAgeGroup: "",
        admissionSoughtFor: "",
        additionalInformation: "",
      });
      setTouched({});
    } catch (err) {
      setSubmitError(err?.message || "Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.shell}>
        <Card className={classes.headerCard}>
          <div className={classes.brandRow}>
            <Box display="flex" alignItems="center" justifyContent="center">
              {schoolLogo ? (
                <img src={schoolLogo} alt="School Logo" className={classes.logo} />
              ) : (
                <Box className={classes.logo} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="textSecondary">
                    Logo
                  </Typography>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="h5" className={classes.title}>
                {isSchoolLoading ? "Loading..." : schoolName || "Enquiry Form"}
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Academic year {ACADEMIC_YEAR} • Date of application: {systemDateText}
              </Typography>
              {schoolError ? (
                <Typography variant="body2" className={classes.danger} style={{ marginTop: 8 }}>
                  {schoolError}
                </Typography>
              ) : null}
            </Box>

            <Box display="flex" justifyContent="center">
              <div className={classes.yearChip}>Academic year {ACADEMIC_YEAR}</div>
            </Box>
          </div>
        </Card>

        <Card className={classes.formCard}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Mandatory details
          </Typography>
          <Typography variant="body2" className={classes.helper}>
            Fields marked as mandatory must be filled before submitting.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Student Name *"
                value={values.studentName}
                onChange={handleChange("studentName")}
                onBlur={markTouched("studentName")}
                error={Boolean(touched.studentName && errors.studentName)}
                helperText={touched.studentName && errors.studentName ? errors.studentName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DOB *"
                type="date"
                value={values.dob}
                onChange={handleChange("dob")}
                onBlur={markTouched("dob")}
                error={Boolean(touched.dob && errors.dob)}
                helperText={touched.dob && errors.dob ? errors.dob : " "}
                variant="outlined"
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl error={Boolean(touched.gender && errors.gender)} component="fieldset">
                <FormLabel component="legend">Gender * (Male / Female)</FormLabel>
                <RadioGroup row value={values.gender} onChange={handleChange("gender")} onBlur={markTouched("gender")}>
                  <FormControlLabel value="Male" control={<Radio />} label="Male" />
                  <FormControlLabel value="Female" control={<Radio />} label="Female" />
                </RadioGroup>
                <FormHelperText>{touched.gender && errors.gender ? errors.gender : " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father name *"
                value={values.fatherName}
                onChange={handleChange("fatherName")}
                onBlur={markTouched("fatherName")}
                error={Boolean(touched.fatherName && errors.fatherName)}
                helperText={touched.fatherName && errors.fatherName ? errors.fatherName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father contact number *"
                value={values.fatherContactNumber}
                onChange={handleChange("fatherContactNumber")}
                onBlur={markTouched("fatherContactNumber")}
                error={Boolean(touched.fatherContactNumber && errors.fatherContactNumber)}
                helperText={touched.fatherContactNumber && errors.fatherContactNumber ? errors.fatherContactNumber : " "}
                variant="outlined"
                autoComplete="off"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother name *"
                value={values.motherName}
                onChange={handleChange("motherName")}
                onBlur={markTouched("motherName")}
                error={Boolean(touched.motherName && errors.motherName)}
                helperText={touched.motherName && errors.motherName ? errors.motherName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother contact number *"
                value={values.motherContactNumber}
                onChange={handleChange("motherContactNumber")}
                onBlur={markTouched("motherContactNumber")}
                error={Boolean(touched.motherContactNumber && errors.motherContactNumber)}
                helperText={touched.motherContactNumber && errors.motherContactNumber ? errors.motherContactNumber : " "}
                variant="outlined"
                autoComplete="off"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email *"
                value={values.email}
                onChange={handleChange("email")}
                onBlur={markTouched("email")}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email ? errors.email : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "18px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Optional details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Classes / Age groups</InputLabel>
                <Select value={values.classOrAgeGroup} onChange={handleChange("classOrAgeGroup")} label="Classes / Age groups">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {CLASS_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Admission sought for</InputLabel>
                <Select value={values.admissionSoughtFor} onChange={handleChange("admissionSoughtFor")} label="Admission sought for">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {ADMISSION_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional information"
                value={values.additionalInformation}
                onChange={handleChange("additionalInformation")}
                variant="outlined"
                multiline
                rows={4}
                autoComplete="off"
              />
            </Grid>
          </Grid>

          {submitError ? (
            <Typography variant="body2" className={classes.danger} style={{ marginTop: 12 }}>
              {submitError}
            </Typography>
          ) : null}

          <div className={classes.footer}>
            <Button
              className={classes.primaryButton}
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={isSubmitting || isSchoolLoading || Boolean(schoolError)}
            >
              {isSubmitting ? (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <CircularProgress size={18} style={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : (
                "Generate enquiry form"
              )}
            </Button>
          </div>
        </Card>
      </div>

      <Snackbar
        open={Boolean(submitSuccess)}
        autoHideDuration={2500}
        onClose={() => setSubmitSuccess("")}
        message={submitSuccess}
      />
    </div>
  );
}

